import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { Quotation } from "../../models/quotation.model";
import { QuotationState } from "../../enums/quotation-state.enum";
import { RolesEnum } from "../../enums/roles.enum";
import { AuthService } from "../../services/auth.service";
import { RestoreQuotationComponent } from "../restore-quotation/restore-quotation.component";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Clipboard } from "@angular/cdk/clipboard";
import { QuotationService } from "../../services/quotation.service";
import { ConfirmEmailComponent } from "../confirm-email/confirm-email.component";
import { DeleteQuotationComponent } from "../delete-quotation/delete-quotation.component";
import { SourceType } from "../../enums/source-type.enum";
import { OrderState } from "../../enums/order-state.enum";
import { OrganizationService } from "../../services/organization.service";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { OrderService } from "../../services/order.service";
import { QuotationInformationComponent } from "../quotation-information/quotation-information.component";

@Component({
    selector: 'app-quotation-menu',
    templateUrl: './quotation-menu.component.html',
    styleUrls: ['./quotation-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuotationMenuComponent {
  @Input() quotation: Quotation;
  @Output() restored = new EventEmitter<boolean>();
  @Output() deleted = new EventEmitter<void>();
  @Output() sent = new EventEmitter<void>();
  @Input() withBackButton = true;
  @Input() tableMode = false;
  @Input() selectable = false;
  location = inject(Location);
  public authService = inject(AuthService);
  public translate = inject(TranslateService);
  public quotationService = inject(QuotationService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private clipboard = inject(Clipboard);
  public organizationService = inject(OrganizationService);
  private router = inject(Router);
  private orderService =  inject(OrderService);
  public progress = false;
  protected readonly QuotationState = QuotationState;
  protected readonly RolesEnum = RolesEnum;
  protected readonly SourceType = SourceType;
  protected readonly OrderState = OrderState;
  zipProgress = false;
  isRotating = false;

  /**
   * Get a public url to validate a quotation
   * @param event The click event
   */
  getSignedUrl(event: MouseEvent): void {
    event.stopPropagation();
    this.progress = true;
    this.quotationService.getSignedUrl(this.quotation.id)
      .then(url => {
        this.clipboard.copy(url);
        const message = this.translate.instant('ADMIN.COPY_LINK_SUCCESS');
        this.snackBar.open(message + '\n\n' + url, 'x', {
          duration: 10000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      })
      .catch(err => {
        console.error(err);
        throw err;
      })
      .finally(() => this.progress = false);
  }

  /**
   * Open restore quotation dialog
   */
  restoreDialog(): void {
    this.dialog.open(RestoreQuotationComponent, { data: this.quotation }).afterClosed()
      .subscribe(restored => this.restored.emit(restored));
  }

  /**
   * Open delete quotation dialog
   */
  deleteDialog(): void {
    this.dialog.open(DeleteQuotationComponent, { data: this.quotation }).afterClosed()
      .subscribe(deleted => {
        if (!deleted) {
          return;
        }
        this.deleted.emit()
      });
  }

  /**
   * Open send email modal
   * @param quotation The selected quotation
   */
  sendEmail(quotation: Quotation): void {
    this.dialog.open(ConfirmEmailComponent, { data: { quotation } });
  }

  /**
   * Downloads a quotation PDF file for the current organization and quotation ID.
   * The method fetches the file contents from the server, creates a temporary URL, and triggers the download.
   * @returns A promise that resolves when the download process completes.
   */
  async download(): Promise<void> {
    this.zipProgress = true;
    try {
      const base64String = await this.quotationService.download(this.quotation.id);
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes.buffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quotation-${this.quotation.erpId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } finally {
      this.zipProgress = false;
    }
  }

  /**
   * Synchronizes the order for the given transaction ID with the organization's order service.
   * @param quotation - The unique identifier for the transaction to be synchronized.
   * @returns A promise that resolves when the order synchronization process completes.
   */
  async sync(quotation: Quotation): Promise<boolean> {
    this.isRotating = true;
    try {
      await this.orderService.syncOrder(this.organizationService.currentOrganization.id, quotation.erpId);
    } catch (error) {
      console.error(error);
    } finally {
      this.isRotating = false;
    }
    const currentUrl = this.router.url;
    return this.router.navigateByUrl(currentUrl);
  }

  /**
   * Navigates to a specific route based on the current organization's ID and the quotation ID.
   * @returns A promise that resolves to a boolean indicating whether navigation was successful.
   */
  goTo(): Promise<boolean> {
    return this.router.navigate([this.organizationService.currentOrganization.oid, 'quotation', this.quotation.id]);
  }

  /**
   * Opens a popup dialog to show the complete quotation information.
   */
  openQuotationInformationDialog(): void {
    this.dialog.open(QuotationInformationComponent, {
      data: { quotation: this.quotation },
      width: '780px',
      maxWidth: '95vw',
      panelClass: 'premium-order-info-dialog'
    });
  }
}
